/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { isFinite } from 'lodash';
import { GpsSaMapLayer } from '../../shared/entities/gpsSaMapLayer';
import { Action, ActionCreators } from './actions';
import { getGpsPositionAccuracyLevel, GpsPositionAccuracyLevel } from '../../utils/GpsSaUtils';

export interface State {
  readonly gpsSaLowAccuracyMapLayers: GpsSaMapLayer[];
  readonly gpsSaReducedAccuracyMapLayers: GpsSaMapLayer[];
  readonly gpsSaNormalAccuracyMapLayers: GpsSaMapLayer[];
  readonly isRequestingGpsSaMapLayers: boolean;
  readonly isGpsSaMapLayersRequestFinished: boolean;
  readonly gpsSaMapLayersRequestError?: string;

  readonly mapLayersETag?: string;
}

export const initialGpsSaState: State = {
  gpsSaLowAccuracyMapLayers: [],
  gpsSaReducedAccuracyMapLayers: [],
  gpsSaNormalAccuracyMapLayers: [],
  isRequestingGpsSaMapLayers: false,
  isGpsSaMapLayersRequestFinished: false,
  gpsSaMapLayersRequestError: undefined,

  mapLayersETag: undefined,
};

export default function reducer(state: State = initialGpsSaState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    //For requesting all orgs
    case ActionCreators.RequestGpsSaMapLayers.type:
      partialState = {
        isRequestingGpsSaMapLayers: true,
        isGpsSaMapLayersRequestFinished: false,
        gpsSaMapLayersRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedGpsSaMapLayersSuccess.type: {
      // Check if the response is 304 Not Modified
      if (action.payload.status === 304) {
        // Update state without updating the map layers
        partialState = {
          isRequestingGpsSaMapLayers: false,
          isGpsSaMapLayersRequestFinished: true,
          gpsSaMapLayersRequestError: undefined,
        };
      } else {
        // Three arrays to hold the map layers with different accuracy levels
        const lowAccuracyMapLayers: GpsSaMapLayer[] = [];
        const reducedAccuracyMapLayers: GpsSaMapLayer[] = [];
        const normalAccuracyMapLayers: GpsSaMapLayer[] = [];

        action.payload.response.forEach(mapLayer => {
          // Skip map layers with invalid NACp values
          if (!isFinite(mapLayer.nacPMode)) {
            return;
          }

          // Get the accuracy level for the current map layer
          const accuracyLevel = getGpsPositionAccuracyLevel(mapLayer.nacPMode);

          // Split up the map layers into the different accuracy levels
          switch (accuracyLevel) {
            case GpsPositionAccuracyLevel.Normal:
              normalAccuracyMapLayers.push(mapLayer);
              break;
            case GpsPositionAccuracyLevel.Reduced:
              reducedAccuracyMapLayers.push(mapLayer);
              break;
            case GpsPositionAccuracyLevel.Low:
              lowAccuracyMapLayers.push(mapLayer);
              break;
            default:
              // This case should not happen since we check for finite NACp values above
              console.warn(`Unexpected accuracy level for H3 hexagon: ${mapLayer.h3Cell}`);
              break;
          }
        });

        // Update state including the map layers
        partialState = {
          gpsSaLowAccuracyMapLayers: lowAccuracyMapLayers,
          gpsSaReducedAccuracyMapLayers: reducedAccuracyMapLayers,
          gpsSaNormalAccuracyMapLayers: normalAccuracyMapLayers,
          isRequestingGpsSaMapLayers: false,
          isGpsSaMapLayersRequestFinished: true,
          gpsSaMapLayersRequestError: undefined,
          mapLayersETag: action.payload.eTag,
        };
      }

      break;
    }

    case ActionCreators.ReceivedGpsSaMapLayersError.type:
      partialState = {
        isRequestingGpsSaMapLayers: false,
        isGpsSaMapLayersRequestFinished: true,
        gpsSaMapLayersRequestError: action.payload.message,
      };
      break;

    case ActionCreators.ClearReceivedGpsSaMapLayersError.type:
      partialState = {
        gpsSaMapLayersRequestError: undefined,
      };
      break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
